import React, { useCallback, useEffect, useState } from "react";

import { Eye, FileText, FolderOpen, Download, ShieldCheck, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { Card } from "@/components/ui/Card";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import ChecklistBuilderModal from "@/pages/admin/compliance/checklists/components/ChecklistBuilderModal";
import { ComplianceService } from "@/services/complianceService";
import { invalidateDedupe } from "@/utils/apiDeduper";
import useLiveResource from "@/hooks/useLiveResource";
import { useAuth } from "@/hooks/useAuth";
import { PERMISSIONS } from "@/permissions/constant";
import { getUserPermissions, hasPermission } from "@/permissions/rbac";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { formatDate } from "@/utils/dateUtils";
import DocumentStageBar from "@/pages/admin/compliance/components/DocumentStageBar";

/**
 * The Compliance tab on a candidate (Connectivity Phase F).
 *
 * A recruiter could see compliance progress only by leaving the candidate for
 * the compliance dashboard — a different screen, a different navigation path
 * and a different mental model. This is the same picture where they already
 * are: which checklist, how far along, and the approved documents.
 *
 * **The ACTIVE run only.** A released run keeps its documents and leaves every
 * live surface (Q-ReleaseVisibility); its record is the RELEASE_CANDIDATE
 * audit row. A candidate whose runs are all released reads as "not currently
 * in compliance", which is true.
 *
 * Two permissions, because seeing that a document was approved and taking a
 * copy of it are different acts: `candidate.view_compliance` gates the tab
 * (the registry does that), `candidate.download_documents` gates every control
 * below. The routes enforce both — a hidden button is not a gate.
 */

const Compliance = ({ candidateId, applicationId }) => {
  const { user } = useAuth();
  const canDownload = hasPermission(PERMISSIONS.CANDIDATE_DOWNLOAD_DOCUMENTS, getUserPermissions(user));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [viewChecklistId, setViewChecklistId] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const res = await ComplianceService.getCandidateComplianceTab(candidateId, applicationId);
      setData(res ?? null);
      setFailed(false);
    } catch (e) {
      console.error(e);
      setData(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [candidateId, applicationId]);

  useEffect(() => {
    load();
  }, [load]);

  // Live on the events the approve/upload paths already emit — no new event.
  // `invalidateDedupe` first: the deduper caches the PROMISE, so a refetch
  // without it re-reads the stale one and the screen never moves.
  useLiveResource({
    keys: ["compliance_candidate", "compliance_document"],
    match: (event) => event?.parent?.candidate === candidateId || event?.parent?.compliance_candidate === data?.complianceCandidateId,
    onChange: () => {
      // The key carries the application (Phase G) — invalidating the
      // bare candidate key would miss the cached promise and the tab would
      // keep rendering the pre-event answer.
      invalidateDedupe(`getCandidateComplianceTab_${candidateId}_${applicationId ?? "none"}`);
      load();
    },
  });

  const download = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessageFormat(e, e?.message || "Failed to download"));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <p className="py-16 text-center text-[13px] text-text-primary/60">Loading compliance…</p>;
  }

  // A read that failed is not the same as a candidate who was never sent, and
  // saying "not in compliance" for an outage would be a confident lie.
  if (failed) {
    return <p className="py-16 text-center text-[13px] text-text-primary/60">Could not load compliance for this candidate. Try again shortly.</p>;
  }

  if (!data?.hasRun) {
    return (
      <Card className="p-6 text-center">
        <ShieldCheck size={20} className="mx-auto mb-2 text-text-primary/40" />
        <p className="text-[13px] font-medium text-text-primary">Not currently in compliance</p>
        <p className="mt-1 text-[12px] text-text-primary/60">No checklist has been assigned yet — this candidate has not been sent to compliance.</p>
      </Card>
    );
  }

  const { progress, folders } = data;

  return (
    <div className="flex flex-col gap-4">
      {/* Header — which checklist, whose job, and a way to read it. */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <ShieldCheck size={16} className="shrink-0 text-text-primary/70" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-text-primary">
              {data.checklistName || "Checklist"}
              {data.checklistVersion ? <span className="ml-1.5 text-[11px] font-normal text-text-secondary">v{data.checklistVersion}</span> : null}
            </p>
            <p className="mt-0.5 text-[12px] text-text-primary/60">
              {data.owningJobTitle ? `In compliance for ${data.owningJobTitle}.` : "This run records no owning job."}
            </p>
            {/* Whose evidence this is (Phase G). A candidate's documents are
                collected ONCE per person, not per job (Q-Grain), so the other
                application's recruiter genuinely sees this run — they were
                just never told it belongs to a different job. */}
            {data.ownedByCallingApplication === false && (
              <p className="mt-1 text-[12px] text-tone-amber">
                {data.owningJobTitle
                  ? `Collected for ${data.owningJobTitle}, not for this application. Documents are collected once per candidate and shared across their applications.`
                  : "Collected for another application. Documents are collected once per candidate and shared across their applications."}
              </p>
            )}
          </div>
          {data.checklistId && (
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              aria-label="View checklist"
              title="View this checklist"
              onClick={() => setViewChecklistId(data.checklistId)}
            >
              <Eye size={14} />
            </Button>
          )}
          {canDownload && folders.some((f) => f.documentCount > 0) && (
            <Button
              type="button"
              className="bg-button-secondary text-text-base"
              disabled={busy === "all"}
              onClick={() => download("all", () => ComplianceService.downloadCandidateDocumentsZip(candidateId, undefined, "compliance-documents.zip"))}
            >
              {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download all
            </Button>
          )}
        </div>
      </Card>

      {/* Progress — this candidate's own, built from their document statuses.
        The bar answers "where is the paperwork"; the line under it answers "is
        this candidate done". Two different questions, so both are spelled out
        rather than collapsed into one percentage. */}
      <Card className="p-4">
        <DocumentStageBar progress={progress} />
        <p className="mt-3 border-t border-border-primary/60 pt-2.5 text-[12px] text-text-primary/60">
          {progress.approved} of {progress.totalSlots} required {progress.totalSlots === 1 ? "document" : "documents"} approved
        </p>
      </Card>

      {/* Folders — one per checklist section, approved documents only. */}
      <div className="flex flex-col gap-3">
        {folders.map((folder) => (
          <Card key={folder.sectionName} className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <FolderOpen size={15} className="shrink-0 text-text-primary/70" />
              <p className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{folder.sectionName}</p>
              <span className="text-[11.5px] text-text-secondary">{folder.documentCount} approved</span>
              {canDownload && folder.documentCount > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-7 px-2 text-[12px]"
                  disabled={busy === folder.sectionName}
                  onClick={() =>
                    download(folder.sectionName, () => ComplianceService.downloadCandidateDocumentsZip(candidateId, folder.sectionName, `${folder.sectionName}.zip`))
                  }
                >
                  {busy === folder.sectionName ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Folder
                </Button>
              )}
            </div>

            {/* An empty section renders with a count of 0 rather than
              vanishing: the absence of an approved document in a required
              section is exactly what someone is looking for. */}
            {folder.documents.length === 0 ? (
              <p className="mt-2 text-[12px] text-text-primary/50">Nothing approved in this section yet.</p>
            ) : (
              <ul className="mt-2 flex flex-col divide-y divide-border-primary/60">
                {folder.documents.map((doc) => (
                  <li key={doc.documentId} className="flex items-center gap-2 py-2">
                    <FileText size={14} className="shrink-0 text-text-primary/50" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12.5px] text-text-primary">{doc.name}</p>
                      <p className="truncate text-[11px] text-text-secondary" title={doc.downloadFilename}>
                        {doc.approvedOn ? `Approved ${formatDate(doc.approvedOn)}` : "Approved"} · {doc.downloadFilename}
                      </p>
                    </div>
                    {canDownload && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        aria-label={`Download ${doc.name}`}
                        title={doc.downloadFilename}
                        disabled={busy === doc.documentId}
                        onClick={() => download(doc.documentId, () => ComplianceService.downloadCandidateDocument(candidateId, doc.documentId, doc.downloadFilename))}
                      >
                        {busy === doc.documentId ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}
      </div>

      {/* A sibling, never nested: `readOnly` turns the builder into the
        Checklist Details screen, so there is no second view modal to drift. */}
      {viewChecklistId && <ChecklistBuilderModal open readOnly checklistId={viewChecklistId} onClose={() => setViewChecklistId(null)} />}
    </div>
  );
};

export default Compliance;
